import React, { useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { styles } from "./style";

export interface MultiSelectOption {
	id: number;
	label: string;
}

interface OwnProps {
	options: MultiSelectOption[];
	selectedIds: number[];
	onChange: (selectedIds: number[]) => void;
	placeholder?: string;
	emptyText?: string;
	error?: boolean;
	helperText?: string;
	/** Cap the number of rows rendered in the dropdown for large catalogs. */
	maxResults?: number;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

const DEFAULT_MAX_RESULTS = 100;

/**
 * Search-and-select multi-select with a "Select All" row, checkbox options, and removable pills
 * below the input — the pattern shown in the MAX-3820 Locations design for the custom city list.
 * Generic over {id, label} so it can be reused beyond cities.
 */
const MultiSelectSearchBase: React.FC<Props> = ({
	options,
	selectedIds,
	onChange,
	placeholder = "Search to add",
	emptyText = "Nothing added yet!",
	error,
	helperText,
	maxResults = DEFAULT_MAX_RESULTS,
	classes = {},
}) => {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const anchorRef = useRef<HTMLDivElement | null>(null);

	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	const optionsById = useMemo(() => {
		const map: { [id: number]: MultiSelectOption } = {};
		options.forEach((o) => { map[o.id] = o; });
		return map;
	}, [options]);

	// Filter the (potentially large) catalog by the typed term and cap the rendered rows.
	const filteredOptions = useMemo(() => {
		const term = inputValue.trim().toLowerCase();
		const matches = term
			? options.filter((o) => o.label.toLowerCase().includes(term))
			: options;
		return matches.slice(0, maxResults);
	}, [options, inputValue, maxResults]);

	// "Select All" reflects/acts on the currently filtered set (matches typical faceted pickers).
	const allFilteredSelected = filteredOptions.length > 0
		&& filteredOptions.every((o) => selectedSet.has(o.id));

	const selectedOptions = useMemo(
		() => selectedIds.map((id) => optionsById[id]).filter(Boolean) as MultiSelectOption[],
		[selectedIds, optionsById]
	);

	const toggleOption = (id: number) => {
		if (selectedSet.has(id)) {
			onChange(selectedIds.filter((sid) => sid !== id));
		} else {
			onChange([...selectedIds, id]);
		}
	};

	const toggleSelectAll = () => {
		if (allFilteredSelected) {
			const filteredIds = new Set(filteredOptions.map((o) => o.id));
			onChange(selectedIds.filter((id) => !filteredIds.has(id)));
		} else {
			const merged = new Set(selectedIds);
			filteredOptions.forEach((o) => merged.add(o.id));
			onChange(Array.from(merged));
		}
	};

	const removeOption = (id: number) => {
		onChange(selectedIds.filter((sid) => sid !== id));
	};

	// ClickAwayListener wraps the WHOLE control (input + dropdown), and the Popper uses disablePortal
	// so its list renders inside that same boundary. Otherwise the click that focuses the field and
	// opens the menu is seen as a click "away" from the (portaled) dropdown and closes it on the same
	// event cycle — the menu flickered open then shut until you started typing. (MAX-3950)
	return (
		<Mui.ClickAwayListener onClickAway={() => setOpen(false)}>
			<Mui.Box>
				<div ref={anchorRef}>
					<Mui.TextField
						fullWidth
						variant="outlined"
						size="small"
						placeholder={placeholder}
						value={inputValue}
						error={error}
						helperText={helperText}
						onChange={(e) => { setInputValue(e.target.value); setOpen(true); }}
						onFocus={() => setOpen(true)}
						InputProps={{
							startAdornment: (
								<Mui.InputAdornment position="start">
									<Icons.Search size={16} />
								</Mui.InputAdornment>
							),
						}}
					/>
				</div>

				<Mui.Popper
					open={open}
					anchorEl={anchorRef.current}
					placement="bottom-start"
					disablePortal
					style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
				>
					<Mui.Paper className={classes.dropdown} elevation={3}>
						<Mui.List dense disablePadding>
							{filteredOptions.length > 0 && (
								<Mui.ListItem button onClick={toggleSelectAll} className={classes.selectAllRow}>
									<Mui.Checkbox
										edge="start"
										color="secondary"
										checked={allFilteredSelected}
										tabIndex={-1}
										disableRipple
									/>
									<Mui.ListItemText primary="Select All" />
								</Mui.ListItem>
							)}
							{filteredOptions.map((option) => (
								<Mui.ListItem button key={option.id} onClick={() => toggleOption(option.id)}>
									<Mui.Checkbox
										edge="start"
										color="secondary"
										checked={selectedSet.has(option.id)}
										tabIndex={-1}
										disableRipple
									/>
									<Mui.ListItemText primary={option.label} />
								</Mui.ListItem>
							))}
							{filteredOptions.length === 0 && (
								<Mui.ListItem>
									<Mui.ListItemText primary="No matches" />
								</Mui.ListItem>
							)}
						</Mui.List>
					</Mui.Paper>
				</Mui.Popper>

				{selectedOptions.length > 0 ? (
					<Mui.Box className={classes.chipContainer}>
						{selectedOptions.map((option) => (
							<Mui.Chip
								key={option.id}
								label={option.label}
								onDelete={() => removeOption(option.id)}
							/>
						))}
					</Mui.Box>
				) : (
					<Mui.Typography variant="body2" className={classes.emptyText}>
						{emptyText}
					</Mui.Typography>
				)}
			</Mui.Box>
		</Mui.ClickAwayListener>
	);
};

export const MultiSelectSearch = withStyles(MultiSelectSearchBase, styles);
